import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getOrderReceipt } from "@/lib/business-tools.functions";
import { formatOrgNumber } from "@/lib/business";
import { formatPrice } from "@/lib/i18n/format";
import { useLocale } from "@/lib/i18n/locale-context";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/kvitto/$id")({
  head: () => ({
    meta: [
      { title: "Kvitto – Sellavo" },
      { name: "description", content: "Kvitto för ett köp med Trygg affär på Sellavo." },
      { property: "og:title", content: "Kvitto – Sellavo" },
      { property: "og:description", content: "Kvitto för ett köp med Trygg affär." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Receipt,
});

const T = {
  sv: { title: "Kvitto", order: "Order", date: "Datum", seller: "Säljare", buyer: "Köpare", org: "Org.nr", vatNo: "Momsnr", item: "Vara", exVat: "exkl. moms", vat: "Moms", shipping: "Frakt", fee: "Serviceavgift", total: "Totalt", print: "Skriv ut / spara PDF", private: "Privatperson – ingen moms" },
  en: { title: "Receipt", order: "Order", date: "Date", seller: "Seller", buyer: "Buyer", org: "Company no.", vatNo: "VAT no.", item: "Item", exVat: "excl. VAT", vat: "VAT", shipping: "Shipping", fee: "Service fee", total: "Total", print: "Print / save PDF", private: "Private seller – no VAT" },
  es: { title: "Recibo", order: "Pedido", date: "Fecha", seller: "Vendedor", buyer: "Comprador", org: "CIF", vatNo: "N.º IVA", item: "Artículo", exVat: "sin IVA", vat: "IVA", shipping: "Envío", fee: "Tarifa de servicio", total: "Total", print: "Imprimir / guardar PDF", private: "Particular – sin IVA" },
};

function Receipt() {
  const { id } = Route.useParams();
  const { lang } = useLocale();
  const tx = T[lang as keyof typeof T] ?? T.sv;
  const fn = useServerFn(getOrderReceipt);
  const { data, error } = useQuery({ queryKey: ["receipt", id], queryFn: () => fn({ data: { id } }) });
  if (error) return <main className="mx-auto max-w-2xl p-8">{(error as Error).message}</main>;
  if (!data) return <main className="mx-auto max-w-2xl p-8" />;
  const { order, seller, country, vat, vatRate, itemExVat } = data;
  const p = (n: number) => formatPrice(n, country);
  const line = (l: string, v: string, bold = false) => (
    <div className={`flex justify-between py-1.5 ${bold ? "border-t border-border pt-3 font-bold" : ""}`}><span>{l}</span><span className="tabular-nums">{v}</span></div>
  );
  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8 print:py-0">
      <div className="rounded-lg border border-border bg-card p-6 print:border-0">
        <h1 className="font-heading text-2xl font-bold">{tx.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{tx.order} {order.order_no} · {tx.date} {new Date(order.paid_at ?? order.created_at).toLocaleDateString(lang)}</p>
        <div className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="font-semibold">{tx.seller}</p>
            {seller ? (
              <>
                <p>{seller.company_name}</p>
                <p>{tx.org} {formatOrgNumber(seller.org_number)}</p>
                {seller.vat_number && <p>{tx.vatNo} {seller.vat_number}</p>}
                {seller.invoice_address && <p>{seller.invoice_address}, {seller.invoice_zip} {seller.invoice_city}</p>}
              </>
            ) : <p className="text-muted-foreground">{tx.private}</p>}
          </div>
          <div>
            <p className="font-semibold">{tx.buyer}</p>
            <p>{order.buyer_name}</p>
            <p className="whitespace-pre-line">{order.buyer_address}</p>
          </div>
        </div>
        <div className="mt-6 text-sm">
          {line(`${tx.item}: ${order.ad_title}${vatRate ? ` (${tx.exVat})` : ""}`, p(vatRate ? itemExVat : order.item_amount))}
          {vatRate > 0 && line(`${tx.vat} ${Math.round(vatRate * 100)} %`, p(vat))}
          {order.shipping_amount > 0 && line(tx.shipping, p(order.shipping_amount))}
          {order.fee_amount > 0 && line(tx.fee, p(order.fee_amount))}
          {line(tx.total, p(order.total_amount), true)}
        </div>
      </div>
      <Button className="mt-4 print:hidden" onClick={() => window.print()}>{tx.print}</Button>
    </main>
  );
}
